import { useQuery } from '@tanstack/react-query';
import { Button, EmptyState, ListGroup, ListRow, Skeleton } from '@uplink/ui';
import { useMemo, useState } from 'react';
import { useTRPC } from '../api.ts';
import { FilterChips } from './common.tsx';

const SOURCE_LABELS: Record<string, string> = {
  api: 'API',
  worker: 'Воркер',
  server: 'Сервер',
  vpn: 'VPN',
  domain: 'Домен',
  payment: 'Платёж',
  integration: 'Интеграция',
};

const CATEGORY_LABELS = {
  server: 'Серверы',
  vpn: 'VPN',
  domain: 'Домены',
  payment: 'Платежи',
  api: 'API',
  worker: 'Фоновые задания',
  other: 'Прочее',
} as const;

type Category = keyof typeof CATEGORY_LABELS;
type Filter = 'all' | Category;

/** Единый журнал технических ошибок и неудачных бизнес-операций. */
export function AdminSystemErrorsScreen() {
  const trpc = useTRPC();
  const errors = useQuery(trpc.admin.systemErrors.queryOptions());
  const [filter, setFilter] = useState<Filter>('all');
  const counts = useMemo(() => {
    const result = {} as Record<Category, number>;
    for (const entry of errors.data ?? []) result[entry.category] = (result[entry.category] ?? 0) + 1;
    return result;
  }, [errors.data]);
  const filters = useMemo(
    () => [
      { value: 'all' as const, label: `Все · ${errors.data?.length ?? 0}` },
      ...(Object.entries(CATEGORY_LABELS) as [Category, string][])
        .filter(([category]) => counts[category])
        .map(([value, label]) => ({ value, label: `${label} · ${counts[value]}` })),
    ],
    [counts, errors.data?.length],
  );
  const visible = filter === 'all' ? errors.data : errors.data?.filter((entry) => entry.category === filter);

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Журнал ошибок</h1>
      <ListGroup footer="Показываются 100 последних ошибок. Ожидаемые ошибки ввода и входа сюда не попадают.">
        <ListRow
          icon="info"
          title="Все технические ошибки в одном месте"
          subtitle="Создание серверов и доменов, установка VPN, пробные ключи, платежи, API, фоновые задания и интеграции."
        />
      </ListGroup>

      {errors.data?.length ? <FilterChips options={filters} value={filter} onChange={setFilter} /> : null}

      {errors.isPending ? (
        <Skeleton height={240} />
      ) : errors.isError ? (
        <EmptyState icon="alert" title="Не удалось загрузить журнал" text={String(errors.error)} />
      ) : visible?.length ? (
        <ListGroup header={filter === 'all' ? 'Последние ошибки' : CATEGORY_LABELS[filter]}>
          {visible.map((entry) => {
            const source = SOURCE_LABELS[entry.source] ?? entry.source;
            const meta = [
              new Date(entry.createdAt).toLocaleString('ru-RU'),
              source,
              entry.operation,
              entry.user,
              entry.context,
            ].filter(Boolean);
            return (
              <ListRow
                key={entry.id}
                icon="alert"
                tone="crit"
                title={entry.message}
                subtitle={`${meta.join(' · ')}${entry.details ? `\n${entry.details}` : ''}`}
              />
            );
          })}
        </ListGroup>
      ) : (
        <EmptyState
          icon="check"
          title={filter === 'all' ? 'Ошибок пока нет' : 'В этой категории ошибок нет'}
          text="Новые технические ошибки появятся здесь."
        />
      )}

      <Button variant="secondary" block loading={errors.isFetching} onClick={() => errors.refetch()}>
        Обновить
      </Button>
    </main>
  );
}
